<template>
  <div class="container mt-4">
    <TopNav
      :title="$t('GENERAL.LABEL.MEETINGS')"
      :showOpenMainNav="true"
      :icon="['far', 'users']"
    >
      <NavItem
        icon="far fa-plus"
        :text="$t('MARKETING.LINKS.NEW_MEETING_TYPE')"
        @click.native="addMeetingType"
      />
    </TopNav>
    <div>
      <div
        v-if="!isLoading && !hasVideoConnection"
        class="alert-danger px-3 py-2 rounded mb-3"
      >
        <span class="c-charcoal">
          <router-link
            :to="{
              name: 'settings-integrations-zoom',
            }"
          >
            {{ $t('MARKETING.TEXT.MISSING_ZOOM_OR_GOOGLE_MEET_CONNECTION') }}
          </router-link>
        </span>
      </div>
      <div
        v-if="!isLoading && !isGoogleCalendarConnected"
        class="alert-danger px-3 py-2 rounded mb-3"
      >
        <span class="c-charcoal">
          <router-link
            :to="{
              name: 'settings-integrations-googlecalendar',
            }"
          >
            {{ $t('MARKETING.TEXT.MISSING_GOOGLE_CALENDAR_CONNECTION') }}
          </router-link>
        </span>
      </div>
    </div>
    <HMTabs v-model="selectedTab" class="mt-4">
      <HMTab name="meeting-types">
        {{ $t('GENERAL.LABEL.MEETING_TYPES') }}
      </HMTab>
      <HMTab name="upcoming">
        {{ $t('MARKETING.LABELS.UPCOMING') }}
      </HMTab>
      <HMTab name="past">
        {{ $t('MARKETING.LABELS.PAST') }}
      </HMTab>
      <HMTab name="cancelled">
        {{ $t('MARKETING.LABELS.CANCELLED') }}
      </HMTab>
    </HMTabs>
    <div v-if="selectedTab === 'meeting-types'" class="mt-4">
      <div class="text-center mt-4" v-if="isLoading">
        <HMSpinner class="spinner" />
      </div>
      <div class="row" v-if="meetingTypes">
        <MeetingTypeCard
          class="col-12 col-md-4 mb-4"
          v-for="meetingType in meetingTypes"
          :key="meetingType.id"
          :meetingType="meetingType"
          @edit="
            $router.push({
              name: 'marketing-meeting-type-edit',
              params: { id: meetingType.id },
            })
          "
          @duplicate="duplicate(meetingType)"
          @delete="showDeleteModal(meetingType)"
        />
      </div>
    </div>
    <div v-show="selectedTab === 'upcoming'" class="mt-4">
      <div class="text-center mt-4" v-if="isLoading">
        <HMSpinner class="spinner" />
      </div>
      <HMSearch v-model="meetingSearch" />
      <HMPaginatedTable
        name="upcoming-meetings-table"
        api="marketing/getUpcomingMeetings"
        :query="query"
        class="mt-4"
        ref="upcoming-meetings-table"
        :pageInQuery="true"
        :perPageFixed="10"
      >
        <template slot-scope="{ data }">
          <div class="mt-4 row">
            <div
              class="col-12"
              v-for="(meetings, date) in groupedMeetingsByDate(
                data.res.results
              )"
              :key="date"
            >
              <h4 class="mb-3">{{ $formatDate(date, 'date-word-long') }}</h4>
              <div
                class="mb-4"
                v-for="meeting in meetings"
                :key="meeting.unique_id"
              >
                <MeetingCard
                  :meeting="meeting"
                  @meetingRescheduled="refreshMeetings"
                  @meetingCancelled="refreshMeetings"
                />
              </div>
              <hr class="mb-4" />
            </div>
          </div>
        </template>
        <template v-slot:no-data>
          <div class="text-center">
            {{ $t('MARKETING.TEXT.NO_UPCOMING_MEETINGS') }}
          </div>
        </template>
      </HMPaginatedTable>
    </div>
    <div v-show="selectedTab === 'past'" class="mt-4">
      <div class="text-center mt-4" v-if="isLoading">
        <HMSpinner class="spinner" />
      </div>
      <HMSearch v-model="meetingSearch" />
      <HMPaginatedTable
        name="past-meetings-table"
        api="marketing/getPastMeetings"
        :query="query"
        class="mt-4"
        ref="past-meetings-table"
        :pageInQuery="true"
        :perPageFixed="10"
      >
        <template slot-scope="{ data }">
          <div class="mt-4 row">
            <div
              class="col-12"
              v-for="(meetings, date) in groupedMeetingsByDate(
                data.res.results
              )"
              :key="date"
            >
              <h4 class="mb-3">{{ $formatDate(date, 'date-word-long') }}</h4>
              <div
                class="mb-4"
                v-for="meeting in meetings"
                :key="meeting.unique_id"
              >
                <MeetingCard :meeting="meeting" />
              </div>
              <hr class="mb-4" />
            </div>
          </div>
        </template>
        <template v-slot:no-data>
          <div class="text-center">
            {{ $t('MARKETING.TEXT.NO_PAST_MEETINGS') }}
          </div>
        </template>
      </HMPaginatedTable>
    </div>
    <div v-show="selectedTab === 'cancelled'" class="mt-4">
      <div class="text-center mt-4" v-if="isLoading">
        <HMSpinner class="spinner" />
      </div>
      <HMSearch v-model="meetingSearch" />
      <HMPaginatedTable
        name="cancelled-meetings-table"
        api="marketing/getCancelledMeetings"
        :query="query"
        class="mt-4"
        ref="cancelled-meetings-table"
        :pageInQuery="true"
        :perPageFixed="10"
      >
        <template slot-scope="{ data }">
          <div class="mt-4 row">
            <div
              class="col-12"
              v-for="(meetings, date) in groupedMeetingsByDate(
                data.res.results
              )"
              :key="date"
            >
              <h4 class="mb-3">{{ $formatDate(date, 'date-word-long') }}</h4>
              <div
                class="mb-4"
                v-for="meeting in meetings"
                :key="meeting.unique_id"
              >
                <MeetingCard :meeting="meeting" />
              </div>
              <hr class="mb-4" />
            </div>
          </div>
        </template>
        <template v-slot:no-data>
          <div class="text-center">
            {{ $t('MARKETING.TEXT.NO_CANCELLED_MEETINGS') }}
          </div>
        </template>
      </HMPaginatedTable>
    </div>
  </div>
</template>

<script>
import { errorToMessage } from '../../../../core/utils/errors';
import TopNav from '@/web/components/navigation/TopNav.vue';
import NavItem from '@/web/components/navigation/NavItem.vue';
import HMDialog from '../../../components/common/HMDialog';
import { mapGetters } from 'vuex';
import MeetingTypeCard from '../../../components/marketing/MeetingTypeCard';
import NewMeetingTypeModal from '../../../components/marketing/NewMeetingTypeModal';
import MeetingAnswersModal from '../../../components/marketing/MeetingAnswersModal';
import MeetingCard from '../../../components/marketing/MeetingCard';
import _ from 'lodash';
import moment from 'moment/moment';

export default {
  name: 'MeetingTypes',
  components: {
    TopNav,
    NavItem,
    MeetingTypeCard,
    MeetingCard,
  },
  computed: {
    ...mapGetters({
      googleCalendars: 'setting/googleCalendars',
      zoomConnections: 'setting/zoomConnections',
      googleMeetConnections: 'setting/googleMeetConnections',
    }),
    isGoogleCalendarConnected() {
      return this.googleCalendars?.length;
    },
    isZoomConnected() {
      return this.zoomConnections?.length;
    },
    isGoogleMeetConnected() {
      return this.googleMeetConnections?.length;
    },
    hasVideoConnection() {
      return this.isZoomConnected || this.isGoogleMeetConnected;
    },
    meetingTableFields() {
      return [
        {
          name: 'full_name',
          title: this.$t('MARKETING.TABLE.FULL_NAME'),
          cssClass: 'h5',
        },
        {
          key: 'email',
          title: this.$t('MARKETING.TABLE.EMAIL'),
        },
        {
          name: 'answers',
          title: this.$t('MARKETING.TABLE.ANSWERS'),
        },
        {
          name: 'meeting_type',
          title: this.$t('MARKETING.TABLE.MEETING_TYPE'),
        },
        {
          name: 'duration',
          title: this.$t('MARKETING.TABLE.DURATION'),
        },
        {
          name: 'scheduled',
          title: this.$t('MARKETING.TABLE.SCHEDULED_AT'),
        },
        {
          name: 'edit',
          shrink: true,
          cssClass: 'p-0',
          disableClick: true,
        },
      ];
    },
    query() {
      return {
        q: this.meetingSearch,
      };
    },
  },
  data() {
    return {
      isLoading: true,
      meetingTypes: null,
      selectedTab: 'meeting-types',
      meetingSearch: '',
    };
  },
  methods: {
    async addMeetingType() {
      this.$modal.show(NewMeetingTypeModal, {
        confirmFn: async newMeetingTypeData => {
          console.log('newMeetingTypeData', newMeetingTypeData);
          try {
            const newMeetingType = await this.$store.dispatch(
              'marketing/addMeetingType',
              newMeetingTypeData
            );
            this.$router.push({
              name: 'marketing-meeting-type-edit',
              params: {
                id: newMeetingType.id,
              },
            });
          } catch (error) {
            this.$notify({
              group: 'app',
              type: 'error',
              title: 'Failed to create a new meeting type',
              text: errorToMessage(error?.response, `Request failed: ${error}`),
              duration: -1, // stay ON
            });
          }
        },
      });
    },
    async duplicate(meeting) {
      try {
        await this.$store.dispatch(
          'marketing/duplicateMeetingType',
          meeting.id
        );
        this.loadData();
      } catch (error) {
        this.$notify({
          group: 'app',
          type: 'error',
          title: 'Failed to delete meeting',
          text: errorToMessage(error?.response, `Request failed: ${error}`),
          duration: -1, // stay ON
        });
      }
    },
    showAnswersModal(meeting) {
      this.$modal.show(MeetingAnswersModal, {
        meeting,
      });
    },
    async showDeleteModal(meetingType) {
      if (this.isDirty) await this.save(true);
      this.$modal.show(HMDialog, {
        title: this.$t('MARKETING.TITLES.DELETE_MEETING_TYPE'),
        text: this.$t('MARKETING.TEXT.CONFIRM_DELETE_MEETING_TYPE', {
          meeting_name: meetingType.name,
        }),
        confirmButton: this.$t('GENERAL.BUTTON.DELETE_MEETING_TYPE'),
        confirm: async () => {
          await this.remove(meetingType);
        },
      });
    },
    async refreshMeetings() {
      this.$refs['upcoming-meetings-table']?.refresh();
      this.$refs['past-meetings-table']?.refresh();
      this.$refs['cancelled-meetings-table']?.refresh();
    },
    async remove(meetingType) {
      try {
        await this.$store.dispatch(
          'marketing/deleteMeetingType',
          meetingType.id
        );
        this.loadData();
      } catch (error) {
        this.$notify({
          group: 'app',
          type: 'error',
          title: 'Failed to delete meeting type',
          text: errorToMessage(error?.response, `Request failed: ${error}`),
          duration: -1, // stay ON
        });
      }
    },
    async loadData() {
      this.isLoading = true;
      try {
        this.meetingTypes = await this.$store.dispatch(
          'marketing/loadMeetingTypes'
        );
      } catch (error) {
        this.$notify({
          group: 'app',
          type: 'error',
          title: 'Failed to load meeting types',
          text: errorToMessage(error?.response, `Request failed: ${error}`),
          duration: -1, // stay ON
        });
      } finally {
        this.isLoading = false;
      }
    },
    groupedMeetingsByDate(meetings) {
      const mappedMeetings = _.map(meetings, function (meeting) {
        meeting.scheduled_date = moment(meeting.start_time).format(
          'YYYY-MM-DD'
        );
        return meeting;
      });
      const result = _.groupBy(mappedMeetings, 'scheduled_date');
      console.log('mappedMeetings', result);
      return result;
    },
  },
  async mounted() {
    await this.loadData();
    // Defer connection status checks until after the main list loads
    // to avoid hitting the nginx per-IP concurrent request limit
    this.$store.dispatch('setting/getZoomConnections');
    this.$store.dispatch('setting/getGoogleMeetConnections');
    this.$store.dispatch('setting/getGoogleCalendarConnections');
  },
};
</script>

<style lang="scss" scoped>
@import '@/web/styles/components-variables.scss';
.status {
  padding: 4px 8px;
  border-radius: $border-radius;
  &--draft {
    background-color: lighten($coral, 29%);
    color: $coral;
  }
  &--sent {
    background-color: lighten($light-green, 15%);
    color: $forest;
  }
  &--scheduled {
    background-color: lighten($brown, 25%);
    color: $white;
  }
}

.dot {
  margin-right: 6px;
  font-size: 0.5rem;
  &--sent {
    color: $light-green;
  }
  &--scheduled {
    color: $brown;
  }
  &--draft {
    color: $light-bubblegum;
  }
}

.line-through {
  text-decoration: line-through;
}
</style>
